"use client";

import { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { Policy, Quote } from "@/shared/types/Quote.interface";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNavDots } from "@/shared/components/ui/carousel";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { CoverageUtils } from "@/shared/utils/CoverageUtils";
import { AddPetDataUtils } from "@/shared/utils/AddPetDataUtils";
import { PlanFilterUtils } from "@/shared/utils/PlanFilterUtils";
import PetCardQuickEnroll from "./PetCardQuickEnroll";
import { Heading } from "./ui/Heading";
import { AnimatePresence, m } from "framer-motion";
import Strings from "../utils/Strings.constants";

interface QuickEnrollPlansProps {
    pets: Policy[];
    newPetName?: string;
}

export default function QuickEnrollPlans({ pets, newPetName }: QuickEnrollPlansProps) {
    const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
    const { appState, updateAppState } = useAppLayerContext();
    const [localSelectedIndex, setLocalSelectedIndex] = useState(appState.quickEnrollSelectedPetIndex ?? 0);
    const { watch, setValue, getValues } = useFormParentContext<Quote>();
    const newPolicies = watch("policies");
    const newPetId = newPolicies?.[0]?.id;
    // ENG-20661: the existing pet's plan is applied to the new pet AS REMAPPED for the quote's rating state
    // (AddPetDataUtils.quickEnrollCoverageSettings). The cards and the price below show that same remapped
    // plan, so a CA customer on a grandfathered $2,500 policy sees the $5,000 the new pet will actually get.
    const ratingState = watch("ratingAddress.state");

    const { petPlans } = appState.petPlansObject || {};
    const { isAnnualBilling, quickEnrollHasCustomized } = appState;

    const petsWithIds = useMemo(
        () =>
            pets.map(pet => ({
                ...pet,
                id: pet.accountId,
                coverageSettings: PlanFilterUtils.remapCoverageSettingsForState(pet.coverageSettings, ratingState)
            })),
        [pets, ratingState]
    );

    const activePetId = petsWithIds[localSelectedIndex]?.id;
    const selectedExistingPet = petsWithIds[localSelectedIndex];
    // The plan as the API reports it (not the display copy above): the helper is the one remap point for what is persisted.
    const selectedExistingCoverage = pets[localSelectedIndex]?.coverageSettings;

    // Sync selected existing pet's coverage settings to the new pet's form values
    // so that CoverageEditor reflects the correct coverage if the user clicks "Customize"
    // Skip if user has already customized coverage (preserve their changes on Back → Customize round-trip)
    useEffect(() => {
        if (quickEnrollHasCustomized) return;
        if (selectedExistingCoverage) {
            // Sync coverage selection (type, id, amounts) from the existing pet, remapped for the rating state,
            // but preserve the new pet's API-returned dates (createDate, startDate, name).
            // Use getValues (snapshot) instead of newPolicies (watched) to avoid a setValue → watch → effect cycle.
            setValue(
                "policies.0.coverageSettings",
                AddPetDataUtils.quickEnrollCoverageSettings({
                    existing: selectedExistingCoverage,
                    currentCoverages: getValues("policies.0.coverageSettings.coverages"),
                    ratingState
                })
            );
        }
    }, [selectedExistingCoverage, ratingState, setValue, getValues, quickEnrollHasCustomized]);

    // Persist selected pet index to appState so it survives step navigation (component remount)
    useEffect(() => {
        updateAppState({ quickEnrollSelectedPetIndex: localSelectedIndex });
    }, [localSelectedIndex, updateAppState]);

    // Restore carousel scroll position on mount if returning to this step
    useEffect(() => {
        if (carouselApi && localSelectedIndex > 0) {
            carouselApi.scrollTo(localSelectedIndex, true);
        }
    }, [carouselApi, localSelectedIndex]);

    const coverageValuesToMatch = useMemo(() => {
        if (!newPetId || !petPlans?.[newPetId]) return [];
        const newPetPlans = petPlans[newPetId];
        const allCoverageOptions = CoverageUtils.formatCustomCoverageData(newPetPlans, isAnnualBilling);
        if (!allCoverageOptions) return [];
        const valuesWithMultipleOptions = CoverageUtils.findKeysWithMultipleOptions(allCoverageOptions);
        return CoverageUtils.convertKeysToMatchCriteria(valuesWithMultipleOptions);
    }, [newPetId, petPlans, isAnnualBilling]);

    const newPetDiscounts = newPolicies?.[0]?.discounts;
    const fallbackDiscounts = selectedExistingPet?.discounts;
    const existingCoverageSettings = selectedExistingPet?.coverageSettings;
    const newPetPriceData = useMemo(() => {
        if (!existingCoverageSettings || !newPetId || !petPlans?.[newPetId]) {
            return null;
        }
        // New pet policy may not have associated discount
        // Fallback uses existing pet's discounts for the new pet so that pricing doesn't fluctuate between pre-selected plan and returned API plan
        const foundDiscounts = newPetDiscounts?.length ? newPetDiscounts : fallbackDiscounts;
        const discounts = foundDiscounts?.filter((d): d is typeof d & { adjustment: number; amount: number } => d.adjustment !== undefined && d.amount !== undefined);
        const result = CoverageUtils.calculateSinglePetPrice({
            coverageSettings: existingCoverageSettings,
            petPlans: petPlans[newPetId],
            coverageValuesToMatch,
            isAnnualBilling,
            policyDiscounts: discounts
        });
        return result;
    }, [existingCoverageSettings, newPetId, petPlans, coverageValuesToMatch, isAnnualBilling, newPetDiscounts, fallbackDiscounts]);

    // Sync calculated price to appState so the bottomWidget PriceInfo displays it
    useEffect(() => {
        if (newPetPriceData && newPetId) {
            const priceEntry = {
                preDiscountPrice: newPetPriceData.finalPrice + newPetPriceData.discountAmount,
                discountAmount: newPetPriceData.discountAmount,
                finalPrice: newPetPriceData.finalPrice
            };
            updateAppState({
                calculatedPriceData: {
                    ...priceEntry,
                    singlePetPriceData: { [newPetId]: priceEntry }
                }
            });
        }
    }, [newPetPriceData, newPetId, updateAppState]);

    return (
        <div>
            <Heading level="h1" className="text-lg font-bold">
                {Strings.QUICK_ENROLL_HEADER}
            </Heading>
            <p>
                {petsWithIds.length === 1 && petsWithIds[0] ? (
                    <>
                        Apply <span className="inline-block max-w-36 truncate align-bottom sm:max-w-full">{petsWithIds[0].name}</span>&apos;s plan to{" "}
                        <span className="inline-block max-w-36 truncate align-bottom sm:max-w-full">{newPetName}</span>.
                    </>
                ) : (
                    <>
                        Apply the same plan from a currently enrolled pet to{" "}
                        <span className="inline-block max-w-36 truncate align-bottom font-bold sm:max-w-full">{newPetName}</span>.
                    </>
                )}
            </p>
            <div className="flex w-full justify-center contain-layout">
                <AnimatePresence mode="popLayout" initial={false}>
                    <m.div key="unlinked" initial={false} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <Carousel
                            className="mt-4 grid auto-cols-[320px] grid-flow-col xs:auto-cols-[380px] sm:auto-cols-[590px] lg:auto-cols-[650px]"
                            disabled={petsWithIds.length <= 1}
                            setApi={api => {
                                if (!api) return;
                                setCarouselApi(api);
                                const debouncedSelect = debounce((index: number) => {
                                    setLocalSelectedIndex(index);
                                }, 300);
                                api.on("select", () => {
                                    debouncedSelect(api.selectedScrollSnap() ?? 0);
                                });
                            }}
                        >
                            <div className="flex flex-col items-center">
                                <CarouselContent className="flex h-full w-[320px] gap-1 xs:w-[380px] sm:w-[590px] lg:w-[650px]" contentWrapperClass="overflow-visible">
                                    {petsWithIds.map((pet, index) => (
                                        <CarouselItem key={pet.id} className="h-full pl-0">
                                            <PetCardQuickEnroll
                                                pet={pet}
                                                disabled={petsWithIds.length <= 1}
                                                activePetId={activePetId}
                                                handleActivePetChange={id => {
                                                    const i = petsWithIds.findIndex(p => p.id === id);
                                                    if (i !== -1) setLocalSelectedIndex(i);
                                                }}
                                                onSelect={() => carouselApi?.scrollTo(index)}
                                            />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                {petsWithIds.length > 1 && <CarouselNavDots className="mt-4" testId="quick-enroll" />}
                            </div>
                        </Carousel>
                    </m.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
